<script lang="ts" setup>
import IconClose from '@/icons/close.svg?component'
import IconPadlock from '@/icons/padlock.svg?component'
import { usePageOverlay } from '@/lib/pageOverlay'
import type { Cart } from '@/lib/types/Cart'
import { onBeforeMount, onBeforeUnmount } from 'vue'
import formatMoney from '@/lib/formatters/money'
import CartItem from './CartItem.vue'
import Message from './Message.vue'
import QuantityControl from './QuantityControl.vue'
import type { WorkState } from '@/lib/types/WorkState'
import { computed } from 'vue'
import type { Product } from '@/lib/types/Product'
import CartUpsellItem from './CartUpsellItem.vue'
import Button from './Button.vue'
import type { PayloadAddToCart } from '@/services/cart'
import { pouchReturnEnvelopeInCart } from '@/modules/cartLogic'
import { shipping } from '@/lib/constants'

const props = defineProps<{
  cart: Cart
  workState: WorkState
  workItem: string | null
  dictionary: Record<string, string>
  recommendations: Product[] | null
  isEmbedded: boolean
  isCheckingOut: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'subscribe', key: string, sellingPlanId: string): void
  (e: 'unsubscribe', key: string): void
  (e: 'remove', key: string): void
  (e: 'changeQuantity', key: string, quantity: number): void
  (e: 'changeSellingPlan', key: string, sellingPlanId: string): void
  (e: 'checkout'): void
  (e: 'addUpsell', payload: PayloadAddToCart): void
  (e: 'updateAttributes', attributes: Record<string, string>): void
  (e: 'addPouchReturnEnvelope', quantity: number): void
}>()

const { show, hide } = usePageOverlay(
  (e) => {
    if (document.querySelector('.PageOverlay') === e.target) {
      emit('close')
    }
  },
  (e) => {
    if (e.key === 'Escape') {
      emit('close')
    }
  },
)

const percentOffFreeShipping = computed(() =>
  props.cart.total_weight >= shipping.free_threshold_grams
    ? 100
    : Math.min(1, props.cart.total_price / shipping.free_threshold_pence) * 100,
)
const amountRemainingForFreeShipping = computed(() =>
  props.cart.total_weight >= shipping.free_threshold_grams
    ? 0
    : shipping.free_threshold_pence - props.cart.total_price,
)

if (!props.isEmbedded) {
  onBeforeMount(show)
  onBeforeUnmount(hide)
}

const updateQuantity = (key: string, value: number) => {
  emit('changeQuantity', key, value)
}

const updateSellingPlan = (key: string, sellingPlanId: string | null) => {
  if (sellingPlanId !== null) {
    emit('changeSellingPlan', key, sellingPlanId)
  } else {
    emit('unsubscribe', key)
  }
}

const subscribe = (key: string, sellingPlanId: string) => {
  emit('subscribe', key, sellingPlanId)
}

const t = (label: string) => {
  return props.dictionary[label] || label
}

const pouchReturnEnvelope = computed(() =>
  pouchReturnEnvelopeInCart(props.cart),
)

const itemsFullPrice = computed(() =>
  props.cart.items.reduce((sum, item) => {
    const compareAt = item.selling_plan_allocation?.compare_at_price
    return (
      sum + (compareAt ? compareAt * item.quantity : item.original_line_price)
    )
  }, 0),
)

const changePouchReturnEnvelopesQuantity = (quantity: number) => {
  if (pouchReturnEnvelope.value) {
    updateQuantity(pouchReturnEnvelope.value.key, quantity)
  } else {
    emit('addPouchReturnEnvelope', quantity)
  }
}
</script>

<template>
  <div class="Cart" :class="{ 'Cart--embedded': isEmbedded }">
    <div class="Cart__Header">
      <h2 class="h3">{{ t('title') }}</h2>
      <button v-if="!isEmbedded" class="Cart__Close" @click="$emit('close')">
        <IconClose />
      </button>
      <div class="Cart__ShippingProgress">
        <p
          class="Cart__ShippingProgress__Message"
          v-if="amountRemainingForFreeShipping > 0"
          v-html="
            t('free_shipping_remaining').replace(
              '{{remaining}}',
              formatMoney(amountRemainingForFreeShipping).toString(),
            )
          "
        ></p>
        <p v-else v-html="t('free_shipping_qualified')"></p>
        <div class="Cart__ShippingProgressMeter">
          <span
            class="Cart__ShippingProgressMeter__Bar"
            :style="`width: ${percentOffFreeShipping}%`"
          ></span>
        </div>
        <p class="Cart__ShippingProgress__Note">
          {{ t('shipping_info') }}
        </p>
      </div>
    </div>
    <div class="Cart__Body">
      <div
        class="Cart__PouchReturn"
        v-if="cart.items.filter((item) => item !== pouchReturnEnvelope).length"
      >
        <p>
          Pre-paid returns envelope
          <br />
          <small>One envelope fits 10+ empty pouches</small>
        </p>
        <QuantityControl
          :value="pouchReturnEnvelope ? pouchReturnEnvelope.quantity : 0"
          @change-quantity="changePouchReturnEnvelopesQuantity"
          :is-busy="workState === 'IN_PROGRESS'"
          :max-value="4"
        ></QuantityControl>
      </div>
      <ul class="Cart__Items" v-if="cart.items.length">
        <li
          v-for="item in cart.items.filter(
            (item) => item !== pouchReturnEnvelope,
          )"
          :key="item.id"
        >
          <CartItem
            :item="item"
            :is-busy="item.key === workItem"
            :subscription-data="
              cart.fullProductData
                ? cart.fullProductData[item.id.toString()]
                : null
            "
            @change-selling-plan="updateSellingPlan"
            @change-quantity="updateQuantity"
            @remove="emit('remove', $event)"
            @subscribe="subscribe"
            :dictionary="dictionary"
          ></CartItem>
        </li>
      </ul>
      <div class="Cart__EmptyMessage" v-else>
        <Message mode="dark">
          <template #content>
            <p v-html="t('empty_message_html')"></p>
          </template>
        </Message>
      </div>
      <div class="Cart__Upsell" v-if="recommendations">
        <h5 class="Cart__Upsell__Title">
          {{
            cart.item_count > 0
              ? t('upsell_title')
              : t('upsell_title_empty_products')
          }}
        </h5>
        <ul>
          <li v-for="product in recommendations" :key="product.id">
            <CartUpsellItem
              :item="product"
              :dictionary="dictionary"
              :is-busy="`upsell::${product.id}` === workItem"
              @add="emit('addUpsell', $event)"
            ></CartUpsellItem>
          </li>
        </ul>
      </div>
    </div>
    <div class="Cart__Footer">
      <div class="Cart__Footer__Total">
        <h5>{{ t('subtotal_label') }} ({{ cart.item_count }} items)</h5>
        <span class="Cart__Footer__Total__Price">
          {{ formatMoney(cart.total_price) }}
          <s v-if="itemsFullPrice > cart.total_price">
            {{ formatMoney(itemsFullPrice) }}
          </s>
        </span>
      </div>
      <Button
        :modifiers="['block']"
        :disabled="isCheckingOut || cart.items.length === 0"
        @click="emit('checkout')"
        :loading="isCheckingOut"
      >
        <IconPadlock class="Cart__Padlock__Icon" />
        <span>{{ t('checkout_cta') }}</span>
      </Button>
    </div>
  </div>
</template>

<style>
.Cart {
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: 21;
  overscroll-behavior: none;
  background-color: var(--color-white);
  box-shadow: 0 0 20px 0 var(--color-shadow-30);
  right: 0;
  top: 5px;
  bottom: 0;
  border-radius: var(--radius-large) var(--radius-large) 0 0;
  width: 100%;
  overflow: hidden;
  @media (--mq-tablet-and-up) {
    width: 90%;
    max-width: 500px;
    border-radius: var(--radius-large) 0 0 var(--radius-large);
    top: 24px;
    bottom: 24px;
  }
}

.Cart.Cart--embedded {
  position: initial;
  width: auto;
  border-radius: var(--radius-large);
  overflow: hidden;
  box-shadow: none;
  margin: 0 0 50px;
  @media (--mq-tablet-and-up) {
    max-width: 800px;
  }
}

.Cart__Header {
  background-color: var(--color-lichen-100);
  padding: 18px 24px;
  flex-grow: 0;
  @media (--mq-tablet-and-up) {
    padding: 24px;
  }
}

.Cart__ShippingProgress__Message {
  margin: 0;
  margin-bottom: 0.5rem;
}

.Cart__ShippingProgressMeter {
  background-color: var(--color-lichen-150);
  border-radius: 3px;
  height: 5px;
  width: 100%;
  position: relative;
}

.Cart__ShippingProgressMeter__Bar {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  border-radius: 3px;
  height: 5px;
  background-color: var(--color-forest-100);
  transition: width 0.3s ease-in-out;
}

.Cart__ShippingProgress__Note {
  font-size: var(--font-size--small);
  margin-bottom: 0;
  margin-top: 0.5rem;
}

.Cart__Body {
  overflow: auto;
  padding: 0;
  flex-grow: 1;
  display: flex;
  flex-flow: column nowrap;
}

.Cart__Items {
  list-style: none;
  padding: 0;
  flex-grow: 1;
}

.Cart__EmptyMessage {
  padding: 24px;
  flex-grow: 1;
}

.Cart__PouchReturn {
  background-color: var(--color-deep-ocean-100);
  color: var(--color-warm-white-100);
  font-size: var(--font-size--small);
  display: flex;
  align-items: center;

  & p {
    margin-bottom: 0;
    padding: 16px 16px 16px 24px;
    flex-grow: 1;

    & a {
      color: var(--color-warm-white-100);
      white-space: nowrap;
    }
  }

  & .QuantityControl {
    flex-shrink: 0;
    margin-right: 24px;
  }
}

.Cart__Upsell {
  padding-top: 24px;
  background-color: var(--color-lichen-25);
  border-radius: var(--radius-large);
  margin: 24px 8px 8px;
  flex-grow: 0;

  & > ul {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
  }
}

.Cart__Upsell__Title {
  margin: 0 16px;
}

.Cart__Close {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 24px;
  right: 24px;
  background-color: var(--color-ocean-100);
  color: var(--color-white);
  border: 0;
  width: 28px;
  height: 28px;
  border-radius: 28px;
  transition: background-color var(--global-transition);
  z-index: 1;

  & svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background-color: var(--color-forest-100);
    cursor: pointer;
  }
}

.Cart__Footer {
  padding: 18px 24px;
  background-color: var(--color-lichen-100);
  flex-grow: 0;
  @media (--mq-tablet-and-up) {
    padding: 24px;
  }
}

.Cart__Footer__Total {
  display: flex;
  margin-bottom: 16px;
}

.Cart__Footer__Total__Price {
  flex-grow: 1;
  text-align: right;
  font-size: var(--font-size--h5);
  font-family: var(--font-family-domaine);
  font-weight: bold;

  & s {
    display: block;
    font-family: var(--font-family-rund);
    font-size: var(--font-size--small);
    font-weight: normal;
    color: var(--color-forest-60);
  }
}

.Cart-enter-active,
.Cart-leave-active {
  transition:
    transform 0.2s cubic-bezier(0.645, 0.045, 0.355, 1),
    opacity 0.1s ease-out;
}

.Cart-enter-from {
  transform: translate(0, 100%);
  @media (--mq-tablet-and-up) {
    transform: translate(100%, 0);
  }
}

.Cart-enter-to {
  transform: translate(0, 0);
}

.Cart-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}

.Cart-leave-to {
  opacity: 0;
  transform: translate(0, 100%);
  @media (--mq-tablet-and-up) {
    transform: translate(100%, 0);
  }
}

.Cart__FreeGiftPromo {
  display: flex;
  background-color: var(--color-lichen-25);
  border-radius: var(--radius-small);
  margin: 16px 16px 0;
  padding: 16px;
  align-items: first-baseline;

  & p {
    margin: 0;
  }

  & svg {
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 16px;
  }
}

.Cart__Padlock__Icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  fill: var(--color-white);
}
</style>
